import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../../styles/ag-grid-custom.css";
import { ColDef } from "ag-grid-community";
import { ModuleRegistry } from "ag-grid-community";
import { CellStyleModule, PaginationModule } from "ag-grid-community";
import { Search, Plus, Trash2, Eye, Edit, BarChart3 } from "lucide-react";
import { toast } from "react-toastify";
import { ProjectPhase, Project } from "../../../types/api";
import ProjectPhaseForm from "./ProjectPhaseForm";
import { BUDGETING_COST_CONTROL } from "../../../constants/modules";
import { usePermissions } from "../../../hooks/usePermission";
import { useProjectPhaseStore, useProjectUtilityStore } from "../../../store/utilitySystemsStore";
import images from "../../../assets/images";
import { useNavigate } from "react-router-dom";

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

const ProjectPhaseList: React.FC = () => {
  const { canCreate, canRead, canUpdate, canDelete } = usePermissions();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState<ProjectPhase | null>(null);
  const [mode, setMode] = useState<"create" | "edit" | "view">("create");
  const [searchTerm, setSearchTerm] = useState("");
  const [projectFilter, setProjectFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const gridRef = useRef<AgGridReact>(null);
  const navigate = useNavigate();

  const { phases, phasesLoading, fetchPhases, deletePhase } = useProjectPhaseStore();
  const { projects, fetchProjects } = useProjectUtilityStore();

  const fetchPhasesData = useCallback(async () => {
    try {
      if (!projects.length) await fetchProjects();
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (projectFilter) params.projectId = projectFilter;
      await fetchPhases(params);
    } catch (error: any) {
      toast.error("Failed to fetch project phases");
      console.error("Error fetching phases:", error);
    }
  }, [fetchPhases, statusFilter, projectFilter, projects.length, fetchProjects]);

  useEffect(() => {
    fetchPhasesData();
  }, [fetchPhasesData]);

  const handleCreate = () => {
    setSelectedPhase(null);
    setMode("create");
    setIsFormOpen(true);
  };

  const handleEdit = (phase: ProjectPhase) => {
    setSelectedPhase(phase);
    setMode("edit");
    setIsFormOpen(true);
  };

  const handleView = (phase: ProjectPhase) => {
    setSelectedPhase(phase);
    setMode("view");
    setIsFormOpen(true);
  };

  const handleDelete = async (phase: ProjectPhase) => {
    if (!window.confirm("Are you sure you want to delete this project phase?")) {
      return;
    }

    try {
      const id = phase.id || phase._id;
      if (!id) return;

      await deletePhase(id);
      toast.success("Project phase deleted successfully");
      fetchPhasesData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to delete project phase");
      console.error("Error deleting phase:", error);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedPhase(null);
    setMode("create");
  };

  const handleFormSuccess = () => {
    handleFormClose();
    fetchPhasesData();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Not Started":
        return "#9E9E9E";
      case "On Track":
        return "#4CAF50";
      case "Over Budget":
        return "#F44336";
      case "Under Budget":
        return "#2196F3";
      default:
        return "#757575";
    }
  };

  const projectsMap = useMemo(() => {
    const map = new Map<string, string>();
    projects.forEach((project) => {
      const id = project.id || project._id;
      if (id) map.set(id, project.name);
    });
    return map;
  }, [projects]);

  const filteredPhases = useMemo(() => {
    let filtered = phases;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (phase) =>
          phase.name?.toLowerCase().includes(search) ||
          projectsMap.get(phase.projectId)?.toLowerCase().includes(search)
      );
    }

    if (projectFilter) {
      filtered = filtered.filter((phase) => phase.projectId === projectFilter);
    }

    if (statusFilter) {
      filtered = filtered.filter((phase) => phase.status === statusFilter);
    }

    return filtered;
  }, [phases, searchTerm, projectFilter, statusFilter, projectsMap]);

  const columnDefs: ColDef[] = [
    {
      headerName: "Project",
      field: "projectId",
      flex: 1.5,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 5px 0 10px", textAlign: "left" },
      cellRenderer: (params: any) => {
        return projectsMap.get(params.data.projectId) || "N/A";
      },
    },
    {
      headerName: "Phase Name",
      field: "name",
      flex: 1.5,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 5px 0 10px", textAlign: "left" },
    },
    {
      headerName: "Estimated Cost",
      field: "estimatedCost",
      flex: 1.2,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => formatCurrency(params.value || 0),
    },
    {
      headerName: "Actual Cost",
      field: "actualCost",
      flex: 1.2,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => formatCurrency(params.value || 0),
    },
    {
      headerName: "Variance",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => {
        const estimated = params.data.estimatedCost || 0;
        const actual = params.data.actualCost || 0;
        const variance = actual - estimated;
        const variancePercent = estimated > 0 ? ((variance / estimated) * 100).toFixed(1) : "0";
        const color = variance > 0 ? "#F44336" : variance < 0 ? "#4CAF50" : "#757575";
        return (
          <span style={{ color, fontWeight: "bold" }}>
            {formatCurrency(variance)} ({variancePercent}%)
          </span>
        );
      },
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: (params) => {
        return {
          color: getStatusColor(params.value),
          fontWeight: "bold",
          padding: "10px 0 0 0",
        };
      },
    },
    {
      headerName: "Action",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0 0 0" },
      cellRenderer: (params: any) => {
        const phase = params.data as ProjectPhase;
        return (
          <div className="flex items-center gap-2 justify-center">
            {canRead(BUDGETING_COST_CONTROL) && (
              <button onClick={() => handleView(phase)} title="View Phase">
                <img className="iconTd" src={images.viewIcon} alt="View" />
              </button>
            )}
            {canUpdate(BUDGETING_COST_CONTROL) && (
              <button onClick={() => handleEdit(phase)} title="Edit Phase">
                <img className="iconTd" src={images.editIcon} alt="Edit" />
              </button>
            )}
            {canDelete(BUDGETING_COST_CONTROL) && (
              <button
                onClick={() => handleDelete(phase)}
                title="Delete Phase"
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 size={18} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="px-2 py-4 mt-14 w-full h-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">
          Budgeting & Cost Control
        </h1>
        <div className="flex gap-3">
          {/* <button
            className="flex items-center justify-center font-semibold gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
            onClick={() => navigate("/utility-systems/budgeting-cost-control/dashboard")}
          >
            <BarChart3 size={18} />
            Dashboard
          </button> */}
          {canCreate(BUDGETING_COST_CONTROL) && (
            <button
              className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white px-4 py-2 rounded-lg hover:bg-[#0d8f8c] transition-colors"
              onClick={handleCreate}
            >
              <Plus size={18} />
              New Phase
            </button>
          )}
        </div>
      </div>

      <div className="bg-[#ffffff] w-[100%] mb-4">
        <div className="px-3 py-3">
          <div className="relative flex justify-between items-center">
            <div className="flex w-full justify-start items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search by Phase Name or Project"
                  className="border-2 border-[#4f5762] px-10 py-2 rounded placeholder:font-semibold placeholder:text-[#4e545d]"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                className="border-2 border-[#4f5762] px-4 py-2 rounded font-semibold text-[#4e545d]"
              >
                <option value="">All Projects</option>
                {projects.map((project) => {
                  const id = project.id || project._id;
                  return (
                    <option key={id} value={id}>
                      {project.name}
                    </option>
                  );
                })}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border-2 border-[#4f5762] px-4 py-2 rounded font-semibold text-[#4e545d]"
              >
                <option value="">All Statuses</option>
                <option value="Not Started">Not Started</option>
                <option value="On Track">On Track</option>
                <option value="Over Budget">Over Budget</option>
                <option value="Under Budget">Under Budget</option>
              </select>
              {(statusFilter || projectFilter || searchTerm) && (
                <button
                  onClick={() => {
                    setStatusFilter("");
                    setProjectFilter("");
                    setSearchTerm("");
                  }}
                  className="px-4 py-2 border-2 border-[#4f5762] rounded hover:bg-gray-100 font-semibold text-[#4e545d]"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#ffffff] w-[100%]">
        <div className="ag-theme-alpine mt-4">
          {phasesLoading ? (
            <div className="text-center py-4">Loading phases...</div>
          ) : (
            <AgGridReact
              ref={gridRef}
              rowData={filteredPhases}
              columnDefs={columnDefs}
              rowHeight={50}
              headerHeight={65}
              paginationPageSize={10}
              pagination={true}
              domLayout="autoHeight"
              defaultColDef={{
                sortable: true,
                resizable: true,
              }}
            />
          )}
        </div>
      </div>

      {isFormOpen && (
        <ProjectPhaseForm
          phase={selectedPhase}
          mode={mode}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default ProjectPhaseList;

